import { formatFactorDataRelease } from '../../utils/factorReleasePresentation';
import {
    formatUserFactorModel,
    normalizeFactorDataModel,
    normalizeUserFactorModel,
} from '../../utils/factorDataOptions';
import { formatBetaSourceLabel } from '../../utils/betaSources';
import { buildPosSizingMarketDataDetail } from '../../utils/marketDataStatus';
import {
    formatPosSizingStaleReasonLabel,
    getUserVisiblePosSizingStaleReasons,
} from '../../context/applicationContextPresentation';
export const ENABLE_HOLDINGS_CALCULATION = true;
export const ENABLE_POSITIONS_FETCH = true;
export const ENABLE_INDUSTRIES_FETCH = true;
export const AUTO_PRICE_REFRESH_POLL_MS = 60 * 1000;
export const FACTOR_DATA_FRESHNESS_POLL_MS = 10 * 60 * 1000;
export const WATCHLIST_QUOTE_POLL_MS = 30 * 1000;
export const OVERVIEW_QUOTE_POLL_MS = 60 * 1000;
export const LIVE_MOVE_EPSILON = 1e-9;
export const AUM_UPDATED_EVENT = 'alphamizer:aum-updated';
export const APPBAR_UPLOAD_CONTEXT_EVENT = 'alphamizer:appbar-upload-context';
export const APPBAR_SYNC_STATE_EVENT = 'alphamizer:appbar-sync-state';
export const APPBAR_CONTEXT_LINEAGE_EVENT = 'alphamizer:appbar-context-lineage';
export const APPBAR_AUTO_SYNC_UPDATE_EVENT = 'alphamizer:appbar-auto-sync-update';
export const APPBAR_UPLOAD_INTERACTION_EVENT = 'alphamizer:appbar-upload-interaction';
export const APPBAR_UPLOAD_MODAL_OPEN_EVENT = 'alphamizer:appbar-upload-modal-open';
export const RISK_CONTEXT_REFRESH_REQUEST_EVENT = 'alphamizer:risk-context-refresh-request';
export const FACTOR_DATA_REFRESH_REQUEST_EVENT = 'alphamizer:factor-data-refresh-request';
export const IDIO_VOL_SOURCE_STANDARD = 'standard';
export const IDIO_VOL_SOURCE_AI = 'ai';
export const WATCHLIST_FACTOR_CONTEXT_UNAVAILABLE_MESSAGE = (
    'Factor data is not available for the current Watchlist.'
);

export function resolveDashboardPageError({
    activeView,
    error,
    factorContextUnavailable,
    watchlistContextStatus = null,
} = {}) {
    if (error) return error;
    if (watchlistContextStatus?.requires_initial_validation === true) return null;
    if (factorContextUnavailable && activeView !== 'watchlist') {
        return WATCHLIST_FACTOR_CONTEXT_UNAVAILABLE_MESSAGE;
    }
    return null;
}

export const isAiIdioVolSource = (value) => (
    value === IDIO_VOL_SOURCE_AI || value === 'yes' || value === true
);

export const normalizeIdioVolSource = (value) => (
    isAiIdioVolSource(value) ? IDIO_VOL_SOURCE_AI : IDIO_VOL_SOURCE_STANDARD
);

const firstNonNullish = (...values) => (
    values.find((value) => value !== null && value !== undefined)
);

export function buildLightweightFactorContext({
    factorModel = null,
    factorDataModel = null,
    factorSnapshotDate = null,
    betaSource = null,
    idioVolSource = IDIO_VOL_SOURCE_STANDARD,
    override = null,
} = {}) {
    const source = override && typeof override === 'object' ? override : {};
    const resolvedIdioVolSource = firstNonNullish(
        source.idioVolSource,
        source.idio_vol_source,
        source.idio_source,
        idioVolSource,
    );
    return {
        factorModel: firstNonNullish(source.factorModel, source.factor_model, source.risk_source, factorModel),
        factorDataModel: firstNonNullish(source.factorDataModel, source.factor_data_model, factorDataModel),
        factorSnapshotDate: firstNonNullish(source.factorSnapshotDate, source.factor_snapshot_date, factorSnapshotDate),
        betaSource: firstNonNullish(source.betaSource, source.beta_source, betaSource),
        idioVolSource: normalizeIdioVolSource(resolvedIdioVolSource),
        factorDataVersionKey: firstNonNullish(
            source.factorDataVersionKey,
            source.factor_data_version_key,
            source.version_key,
            source.load_info?.factor_data_version_key,
        ),
        factorInventoryDigest: firstNonNullish(
            source.factorInventoryDigest,
            source.factor_inventory_digest,
            source.load_info?.factor_inventory_digest,
        ),
        matrixScopeVersionKey: firstNonNullish(
            source.matrixScopeVersionKey,
            source.matrix_scope_version_key,
            source.load_info?.matrix_scope_version_key,
        ),
        watchlistIdentityScopeVersion: firstNonNullish(
            source.watchlistIdentityScopeVersion,
            source.watchlist_identity_scope_version,
            source.load_info?.watchlist_identity_scope_version,
        ),
    };
}

export function buildFactorMatrixScopePayload(context = null) {
    if (!context || typeof context !== 'object') {
        return {};
    }
    const matrixScopeVersionKey = normalizeText(
        context.matrixScopeVersionKey ?? context.matrix_scope_version_key,
    );
    const hasVerifiedMatrixScopeVersionKey = (
        matrixScopeVersionKey &&
        !matrixScopeVersionKey.startsWith('factor_context|')
    );
    const payload = {
        factor_data_version_key: normalizeText(
            context.factorDataVersionKey ?? context.factor_data_version_key,
        ),
        factor_inventory_digest: normalizeText(
            context.factorInventoryDigest ?? context.factor_inventory_digest,
        ),
        matrix_scope_version_key: hasVerifiedMatrixScopeVersionKey ? matrixScopeVersionKey : '',
        watchlist_identity_scope_version: hasVerifiedMatrixScopeVersionKey
            ? normalizeText(
                context.watchlistIdentityScopeVersion ?? context.watchlist_identity_scope_version,
            )
            : '',
    };
    return Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value),
    );
}

export function buildFactorContextFromMatrixMetadata({
    response = null,
    factorModel = null,
    factorDataModel = null,
    factorSnapshotDate = null,
    betaSource = null,
    idioVolSource = IDIO_VOL_SOURCE_STANDARD,
    reason = 'matrix_metadata',
} = {}) {
    const baseContext = buildLightweightFactorContext({
        factorModel,
        factorDataModel,
        factorSnapshotDate,
        betaSource,
        idioVolSource,
        override: response,
    });
    const effectiveFactorModel = normalizeUserFactorModel(baseContext.factorModel);
    const effectiveFactorDataModel = normalizeFactorDataModel(
        effectiveFactorModel,
        baseContext.factorDataModel,
    );
    const effectiveSnapshotDate = normalizeText(
        baseContext.factorSnapshotDate ??
        response?.effective_factor_snapshot_date ??
        response?.latest_factor_snapshot_date,
    ) || null;
    const status = normalizeText(response?.status).toUpperCase();
    const available = response?.success !== false && status !== 'ERROR';
    const contextKey = normalizeText(
        response?.matrix_scope_version_key ??
        response?.load_info?.matrix_scope_version_key ??
        response?.cache_key ??
        response?.load_info?.cache_key,
    ) || [
        'factor_context',
        effectiveFactorModel,
        effectiveFactorDataModel ?? '',
        effectiveSnapshotDate ?? '',
        normalizeIdioVolSource(baseContext.idioVolSource),
    ].join('|');

    return {
        factorModel: formatUserFactorModel(effectiveFactorModel),
        factorDataModel: effectiveFactorDataModel,
        factorSnapshotDate: effectiveSnapshotDate,
        betaSource: baseContext.betaSource,
        idioVolSource: normalizeIdioVolSource(baseContext.idioVolSource),
        available,
        verifiedAvailable: available,
        loadable: available,
        matrixScopeVersionKey: contextKey,
        versionKey: normalizeText(baseContext.factorDataVersionKey) || contextKey,
        factorDataVersionKey: normalizeText(baseContext.factorDataVersionKey),
        factorInventoryDigest: normalizeText(baseContext.factorInventoryDigest),
        watchlistIdentityScopeVersion: normalizeText(baseContext.watchlistIdentityScopeVersion),
        status: response?.status ?? null,
        reason,
        coverage: response?.coverage ?? null,
        identityResolution: response?.identity_resolution ?? null,
        idioVolResolution: response?.idio_vol_resolution ?? null,
        fallbackSummary: response?.fallback_summary ?? null,
        aiModelMetadata: response?.ai_model_metadata ?? null,
        loadInfo: response?.load_info ?? null,
        cacheStatus: response?.cache_status ?? response?.load_info?.cache_status ?? null,
        createdAt: new Date().toISOString(),
    };
}

export function buildTrackedQuoteIdentities({
    activeView,
    watchlistRows = [],
    actualHoldings = [],
    selectedHoldings = [],
} = {}) {
    const rows = activeView === 'watchlist'
        ? watchlistRows
        : (activeView === 'overview' ? [...actualHoldings, ...selectedHoldings] : []);
    const identitiesByKey = new Map();
    rows.forEach((row) => {
        const cogniveId = row?.cognive_id == null ? '' : String(row.cognive_id).trim();
        if (cogniveId) identitiesByKey.set(cogniveId, { cognive_id: cogniveId });
    });
    return [...identitiesByKey.values()];
}

export function hasReadyWatchlistCogniveRows(rows = []) {
    return Array.isArray(rows) && rows.some((row) => {
        const cogniveId = row?.cognive_id;
        return typeof cogniveId === 'string'
            ? cogniveId.trim().length > 0
            : cogniveId != null && String(cogniveId).trim().length > 0;
    });
}

const formatIdioVolSourceLabel = (value) => (
    value == null || String(value).trim() === ''
        ? 'Unknown'
        : (normalizeIdioVolSource(value) === IDIO_VOL_SOURCE_AI ? 'AI' : 'Standard')
);

const formatFactorSnapshotDateLabel = (value) => {
    const normalized = value == null ? '' : String(value).trim();
    return normalized || 'Unknown';
};

const normalizeText = (value) => (
    value == null ? '' : String(value).trim()
);

export function normalizeFactorDataStatusPayload(payload) {
    if (!payload || typeof payload !== 'object' || payload.success === false) {
        return null;
    }
    return {
        ...payload,
        factor_model: normalizeUserFactorModel(payload.factor_model ?? payload.risk_source),
        factor_data_model: normalizeText(payload.factor_data_model),
        latest_factor_snapshot_date: normalizeText(payload.latest_factor_snapshot_date),
        latest_ready_at: normalizeText(payload.latest_ready_at),
        version_key: normalizeText(payload.version_key),
        matrix_scope_version_key: normalizeText(payload.matrix_scope_version_key),
        watchlist_identity_scope_version: normalizeText(payload.watchlist_identity_scope_version),
    };
}

export function getFactorContextKey(context) {
    if (!context || typeof context !== 'object') {
        return '';
    }
    return normalizeText(
        context.matrixScopeVersionKey ??
        context.matrix_scope_version_key ??
        context.versionKey ??
        context.version_key ??
        context.contextKey,
    );
}

export function buildFactorContextFromStatus({
    status = null,
    factorModel,
    factorDataModel,
    factorSnapshotDate = null,
    reason = 'factor_data_status',
    previousContext = null,
} = {}) {
    const normalizedStatus = normalizeFactorDataStatusPayload(status);
    const normalizedFactorModel = normalizeUserFactorModel(
        normalizedStatus?.factor_model ?? factorModel,
    );
    const normalizedFactorDataModel = normalizeFactorDataModel(
        normalizedFactorModel,
        normalizedStatus?.factor_data_model ?? factorDataModel,
    );
    const normalizedSnapshotDate = normalizeText(
        normalizedStatus?.latest_factor_snapshot_date ?? factorSnapshotDate,
    ) || null;
    const versionKey = normalizeText(normalizedStatus?.version_key);
    const watchlistIdentityScopeVersion = normalizeText(
        normalizedStatus?.watchlist_identity_scope_version,
    );
    const matrixScopeVersionKey = normalizeText(
        normalizedStatus?.matrix_scope_version_key,
    );
    const hasVerifiedAvailableStatus = Boolean(
        normalizedStatus?.available === true &&
        (matrixScopeVersionKey || versionKey),
    );
    const fallbackKey = [
        'factor_context',
        normalizedFactorModel,
        normalizedFactorDataModel ?? '',
        normalizedSnapshotDate ?? '',
        watchlistIdentityScopeVersion || previousContext?.watchlistIdentityScopeVersion || '',
    ].join('|');

    return {
        factorModel: formatUserFactorModel(normalizedFactorModel),
        factorDataModel: normalizedFactorDataModel,
        factorSnapshotDate: normalizedSnapshotDate,
        latestReadyAt: normalizeText(normalizedStatus?.latest_ready_at) || null,
        versionKey,
        factorDataVersionKey: versionKey,
        factorInventoryDigest: normalizeText(normalizedStatus?.factor_inventory_digest),
        matrixScopeVersionKey: matrixScopeVersionKey || versionKey || fallbackKey,
        watchlistIdentityScopeVersion,
        available: normalizedStatus?.available === true,
        verifiedAvailable: hasVerifiedAvailableStatus,
        loadable: hasVerifiedAvailableStatus,
        status: normalizedStatus?.status ?? null,
        reason,
        sourceStatus: normalizedStatus,
        createdAt: new Date().toISOString(),
    };
}

export function isFactorContextLoadable(context) {
    return Boolean(
        getFactorContextKey(context) &&
        context?.available === true &&
        context?.verifiedAvailable === true,
    );
}

export function isReadyFactorMatrixRefreshResult(result) {
    if (!result || typeof result !== 'object') {
        return false;
    }
    const matrixStatus = normalizeText(result.matrixStatus).toLowerCase();
    return (
        matrixStatus === 'ready' &&
        Boolean(
            result.factorContextOverride ||
            result.matricesOverride ||
            result.factorContext ||
            result.response,
        )
    );
}

export function isReadyWatchlistPublicationRefreshResult(result) {
    const matrixStatus = normalizeText(result?.matrixStatus).toLowerCase();
    if (matrixStatus === 'empty_watchlist') {
        return Boolean(
            !result?.matricesOverride
            && result?.watchlistSurface?.source === 'empty'
            && result?.watchlistSurface?.rowCount === 0
            && normalizeText(result?.watchlistSurface?.matrixStatus).toLowerCase()
                === 'empty_watchlist'
        );
    }
    if (!isReadyFactorMatrixRefreshResult(result)) {
        return false;
    }
    const watchlistSurface = result.watchlistSurface;
    if (!watchlistSurface || typeof watchlistSurface !== 'object') {
        return false;
    }
    if (watchlistSurface.source === 'matrix') {
        return watchlistSurface.riskLoaded === true;
    }
    return false;
}

export function isPendingFactorMatrixRefreshResult(result) {
    if (!result || typeof result !== 'object') {
        return false;
    }
    return normalizeText(result.matrixStatus).toLowerCase() === 'pending';
}

export function factorContextsUseSameSelection(left, right) {
    if (!left || !right) {
        return false;
    }
    const leftFactorModel = normalizeUserFactorModel(left.factorModel ?? left.factor_model);
    const rightFactorModel = normalizeUserFactorModel(right.factorModel ?? right.factor_model);
    return (
        leftFactorModel === rightFactorModel &&
        normalizeFactorDataModel(leftFactorModel, left.factorDataModel ?? left.factor_data_model) ===
            normalizeFactorDataModel(rightFactorModel, right.factorDataModel ?? right.factor_data_model)
    );
}

// The published vendor's saved model moves the published Watchlist to the
// context it selects in the same server transaction. A saved selection the page
// has not accepted is therefore already published, and the accepted context is
// superseded until the page accepts the saved one.
export function isPublishedFactorSettingAwaitingAcceptance({
    publishedContextStatus,
    acceptedFactorContextKey,
    configuredContext,
    activeContext,
}) {
    return Boolean(
        acceptedFactorContextKey
        && publishedContextStatus?.status !== 'no_published_watchlist'
        && !factorContextsUseSameSelection(configuredContext, activeContext),
    );
}

export function factorContextsUseSameFactorDataSnapshot(left, right) {
    if (!factorContextsUseSameSelection(left, right)) {
        return false;
    }
    const leftSnapshot = normalizeText(
        left.factorSnapshotDate ??
        left.factor_snapshot_date ??
        left.latest_factor_snapshot_date,
    );
    const rightSnapshot = normalizeText(
        right.factorSnapshotDate ??
        right.factor_snapshot_date ??
        right.latest_factor_snapshot_date,
    );
    return Boolean(leftSnapshot && rightSnapshot && leftSnapshot === rightSnapshot);
}

export function factorContextsUseSameFactorGeneration(left, right) {
    if (!factorContextsUseSameFactorDataSnapshot(left, right)) {
        return false;
    }
    const leftDigest = normalizeText(
        left.factorInventoryDigest ?? left.factor_inventory_digest,
    );
    const rightDigest = normalizeText(
        right.factorInventoryDigest ?? right.factor_inventory_digest,
    );
    if (leftDigest && rightDigest) {
        return leftDigest === rightDigest;
    }

    const releaseVersion = (context) => {
        const version = normalizeText(
            context.factorDataVersionKey ??
            context.factor_data_version_key ??
            context.versionKey ??
            context.version_key,
        );
        if (!version || version.startsWith('factor_context|')) {
            return '';
        }
        if (!version.startsWith('manifest|')) {
            return version;
        }
        const factorStart = version.indexOf('|factor=');
        const msiStart = version.indexOf('|msi=', factorStart + 1);
        return factorStart >= 0 && msiStart > factorStart
            ? version.slice(factorStart + 1, msiStart)
            : '';
    };
    const leftReleaseVersion = releaseVersion(left);
    const rightReleaseVersion = releaseVersion(right);
    // The matrix-scope key also includes the current Watchlist identity set, so
    // it must not define Factor Data freshness. When digest metadata is absent,
    // compare the factor-only release key. Manifest version keys also contain
    // MSI and market inputs, so retain only their factor source segment.
    return Boolean(
        leftReleaseVersion &&
        rightReleaseVersion &&
        leftReleaseVersion === rightReleaseVersion
    );
}

export function inheritFactorGenerationDigest(context, ...candidates) {
    if (!context || typeof context !== 'object') {
        return context;
    }
    const currentDigest = normalizeText(
        context.factorInventoryDigest ?? context.factor_inventory_digest,
    );
    if (currentDigest) {
        return context;
    }
    const source = candidates.find((candidate) => (
        candidate &&
        factorContextsUseSameFactorDataSnapshot(context, candidate) &&
        normalizeText(
            candidate.factorInventoryDigest ?? candidate.factor_inventory_digest,
        )
    ));
    if (!source) {
        return context;
    }
    return {
        ...context,
        factorInventoryDigest: normalizeText(
            source.factorInventoryDigest ?? source.factor_inventory_digest,
        ),
    };
}

export function shouldPromoteVerifiedFactorContext({
    acceptedContext = null,
    nextContext = null,
} = {}) {
    return Boolean(
        acceptedContext &&
        nextContext?.verifiedAvailable === true &&
        acceptedContext?.verifiedAvailable !== true &&
        factorContextsUseSameFactorDataSnapshot(acceptedContext, nextContext),
    );
}

export function formatFactorDataContextSummary(context = {}) {
    if (!context) {
        return 'Unknown';
    }
    const factorModelLabel = context?.factorModel
        ? formatUserFactorModel(context.factorModel)
        : 'Unknown';
    const factorDataModelLabel = context?.factorDataModel || 'Unknown';
    const factorSnapshotDateLabel = formatFactorSnapshotDateLabel(context?.factorSnapshotDate);
    return [
        factorModelLabel,
        factorDataModelLabel,
        factorSnapshotDateLabel,
    ].join(' / ');
}

export function buildFactorDataFreshnessSummary({
    acceptedContext = null,
    pendingContext = null,
    loading = false,
    availableStatus = null,
} = {}) {
    const normalizedAvailableStatus = normalizeFactorDataStatusPayload(availableStatus);
    const hasPending = Boolean(pendingContext && getFactorContextKey(pendingContext));
    const acceptedSnapshot = acceptedContext?.factorSnapshotDate || null;
    const pendingSnapshot = pendingContext?.factorSnapshotDate || null;
    const pendingReadyToLoad = hasPending && isFactorContextLoadable(pendingContext);
    const pendingHasVerifiedStatus = hasPending && Boolean(pendingContext?.sourceStatus);
    const pendingNotReady = hasPending && pendingHasVerifiedStatus && !pendingReadyToLoad;
    const pendingChecking = hasPending && !pendingReadyToLoad && !pendingNotReady;

    if (loading && !hasPending) {
        return {
            visible: true,
            contextState: 'loading',
            loading: true,
            statusColor: 'info',
            statusLabel: 'Factor Data: Loading',
            freshnessLabel: 'Loading',
            detailText: 'Loading factor data for the active dashboard context.',
            showLoadAction: false,
            metadataChips: [],
        };
    }

    if (hasPending) {
        const changeLabel = pendingContext.reason === 'settings_changed'
            ? 'Factor settings changed'
            : pendingContext.reason === 'inventory_changed'
                ? 'Inventory changed'
                : pendingContext.reason === 'identity_scope_changed'
                    ? 'MSI identity scope changed'
                    : 'Factor context changed';
        return {
            visible: true,
            contextState: pendingReadyToLoad
                ? 'update_available'
                : pendingChecking
                    ? 'checking'
                    : 'not_ready',
            loading,
            isStale: true,
            statusColor: pendingReadyToLoad ? 'warning' : pendingChecking ? 'info' : 'warning',
            statusLabel: pendingReadyToLoad
                ? 'Factor Data: Update Available'
                : pendingChecking
                    ? 'Factor Data: Checking'
                    : 'Factor Data: Not Ready',
            freshnessLabel: pendingReadyToLoad
                ? 'Update Available'
                : pendingChecking
                    ? 'Checking'
                    : 'Not Ready',
            detailText: pendingReadyToLoad
                ? 'A newer or different factor-data context is ready. The dashboard will keep using the accepted context until you load it.'
                : pendingChecking
                    ? 'Checking factor-data availability for the selected context. The dashboard is still using the last accepted factor data.'
                    : 'The selected factor context is not ready yet. The dashboard is still using the last accepted factor data.',
            acceptedContextSummary: acceptedContext
                ? formatFactorDataContextSummary(acceptedContext)
                : null,
            pendingContextSummary: formatFactorDataContextSummary(pendingContext),
            showLoadAction: pendingReadyToLoad,
            showRemindAction: true,
            metadataChips: [
                { label: changeLabel, color: pendingChecking ? 'info' : 'warning', variant: 'outlined' },
                ...(acceptedSnapshot ? [{
                    label: `Current ${acceptedSnapshot}`,
                    variant: 'outlined',
                }] : []),
                ...(pendingSnapshot ? [{
                    label: `Available ${pendingSnapshot}`,
                    color: 'warning',
                    variant: 'outlined',
                }] : []),
            ],
        };
    }

    if (!acceptedContext && normalizedAvailableStatus?.available === false) {
        return {
            visible: true,
            contextState: 'not_ready',
            loading: false,
            isStale: true,
            statusColor: 'warning',
            statusLabel: 'Factor Data: Not Ready',
            freshnessLabel: 'Not Ready',
            detailText: 'Factor data for the selected context is not ready yet.',
            acceptedContextSummary: null,
            pendingContextSummary: null,
            showLoadAction: false,
            metadataChips: [
                ...(normalizedAvailableStatus.latest_factor_snapshot_date ? [{
                    label: `Snapshot ${normalizedAvailableStatus.latest_factor_snapshot_date}`,
                    variant: 'outlined',
                }] : []),
            ],
        };
    }

    return {
        visible: Boolean(acceptedContext || normalizedAvailableStatus),
        contextState: acceptedContext || normalizedAvailableStatus ? 'current' : 'checking',
        loading: false,
        isStale: false,
        statusColor: 'success',
        statusLabel: 'Factor Data: Current',
        freshnessLabel: 'Current',
        detailText: acceptedContext
            ? 'The active dashboard is using the accepted factor-data context.'
            : 'Factor data is current.',
        acceptedContextSummary: acceptedContext
            ? formatFactorDataContextSummary(acceptedContext)
            : null,
        pendingContextSummary: null,
        showLoadAction: false,
        metadataChips: [
            ...(acceptedSnapshot ? [{
                label: `Snapshot ${acceptedSnapshot}`,
                variant: 'outlined',
            }] : []),
        ],
    };
}

const formatPosSizingFactorDataModel = (value) => {
    const normalized = normalizeText(value);
    return normalized ? normalized.toUpperCase() : 'Unknown';
};

const contextValuesMatch = (left, right) => (
    String(left ?? '').trim().toLowerCase() === String(right ?? '').trim().toLowerCase()
);

const formatPosSizingBetaSource = (value) => (
    normalizeText(value) ? formatBetaSourceLabel(value) : 'Unknown'
);

export function buildPosSizingContextComparisonRows(savedContext, currentContext, status) {
    const saved = savedContext && typeof savedContext === 'object' ? savedContext : {};
    const current = currentContext && typeof currentContext === 'object' ? currentContext : {};
    const savedContextAvailable = status?.optimizer_context?.availability === 'available'
        && status?.optimizer_context?.runnability === 'runnable';
    const mismatches = Array.isArray(status?.context_mismatches) ? status.context_mismatches : [];
    const fieldMatches = (field) => {
        if (!savedContextAvailable || !normalizeText(saved[field]) || !normalizeText(current[field])) return null;
        const mismatch = mismatches.find(item => item?.field === field);
        // Missing evidence is not a proved difference, even when it is listed
        // as a context-metadata mismatch by the backend.
        if (mismatch && (!normalizeText(mismatch.saved) || !normalizeText(mismatch.current))) return null;
        return mismatch ? false : contextValuesMatch(saved[field], current[field]);
    };
    const watchlistAttributesChanged = getUserVisiblePosSizingStaleReasons(
        status?.optimizer_context?.stale_reasons,
    ).includes('classification');
    const factorDataReleaseChange = (
        Array.isArray(status?.context_mismatches) ? status.context_mismatches : []
    ).find((mismatch) => mismatch?.field === 'factor_data_release');
    const releaseComparison = status?.factor_data_release_comparison || factorDataReleaseChange;
    const actualAdditionCount = Number(status?.actual_missing_from_pos_sizing_count ?? 0);
    const actualRemovalCount = getPosSizingActualRemovalCount(status);
    const actualMembershipChanged = Boolean(
        status?.requires_actual_sync || actualAdditionCount > 0 || actualRemovalCount > 0
    );
    const actualMembershipChange = [
        actualAdditionCount > 0
            ? `${actualAdditionCount} addition${actualAdditionCount === 1 ? '' : 's'}`
            : null,
        actualRemovalCount > 0
            ? `${actualRemovalCount} removal${actualRemovalCount === 1 ? '' : 's'}`
            : null,
    ].filter(Boolean).join(' · ')
        || (isUnconsumedActualReimport(status) ? 'Imported again' : 'Actual portfolio changed');
    const rows = [
        ...(actualMembershipChanged ? [{
            key: 'actual_portfolio_membership',
            label: 'Actual portfolio membership',
            savedValue: 'Saved draft membership',
            currentValue: actualMembershipChange,
            matches: false,
        }] : []),
        ...(watchlistAttributesChanged ? [{
            key: 'watchlist_attributes',
            label: 'Watchlist attributes',
            savedValue: 'Saved draft snapshot',
            currentValue: 'Changed in Watchlist',
            matches: false,
        }] : []),
        {
            key: 'factor_provider',
            label: 'Factor provider',
            savedValue: saved.factor_model ? formatUserFactorModel(saved.factor_model) : 'Unknown',
            currentValue: current.factor_model ? formatUserFactorModel(current.factor_model) : 'Unknown',
            matches: fieldMatches('factor_model'),
        },
        {
            key: 'factor_model',
            label: 'Factor model',
            savedValue: formatPosSizingFactorDataModel(saved.factor_data_model),
            currentValue: formatPosSizingFactorDataModel(current.factor_data_model),
            matches: fieldMatches('factor_data_model'),
        },
        {
            key: 'factor_data_date',
            label: 'Factor data date',
            savedValue: formatFactorSnapshotDateLabel(saved.factor_snapshot_date),
            currentValue: formatFactorSnapshotDateLabel(current.factor_snapshot_date),
            matches: fieldMatches('factor_snapshot_date'),
        },
        ...(releaseComparison ? [{
            key: 'factor_data_release',
            label: 'Factor data release',
            savedValue: formatFactorDataRelease(releaseComparison.saved),
            currentValue: formatFactorDataRelease(releaseComparison.current),
            matches: releaseComparison.matches ?? false,
        }] : []),
        {
            key: 'beta_source',
            label: 'Beta source',
            savedValue: formatPosSizingBetaSource(saved.beta_source),
            currentValue: formatPosSizingBetaSource(current.beta_source),
            matches: fieldMatches('beta_source'),
        },
        {
            key: 'idio_risk_source',
            label: 'Idiosyncratic risk',
            savedValue: formatIdioVolSourceLabel(saved.idio_vol_source),
            currentValue: formatIdioVolSourceLabel(current.idio_vol_source),
            matches: fieldMatches('idio_vol_source'),
        },
        {
            key: 'prices_as_of',
            label: 'Prices as of',
            savedValue: formatSyncTimestamp(saved.price_watermark),
            currentValue: formatSyncTimestamp(status?.latest_security_updated_at),
            matches: savedContextAvailable && parseQuoteTimestamp(saved.price_watermark)
                && parseQuoteTimestamp(status?.latest_security_updated_at)
                && typeof status?.is_price_stale === 'boolean'
                ? !status.is_price_stale : null,
        },
    ];

    return rows;
}

export function formatDateTime(date) {
    if (!date) return '';

    return date.toLocaleString('en-US', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

export function parseQuoteTimestamp(value) {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    const raw = String(value).trim();
    if (!raw) return null;

    const hasTimezone = /(Z|[+-]\d{2}:\d{2})$/i.test(raw);
    const normalized = hasTimezone ? raw : `${raw}Z`;
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed;
    }

    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
}

export function normalizePriceSource(value) {
    const source = value == null ? '' : String(value).trim().toLowerCase();
    if (source === 'intraday') return 'intraday';
    if (source === 'daily_close') return 'daily_close';
    return null;
}

export function normalizeMarketStatus(value) {
    const status = value == null ? '' : String(value).trim().toLowerCase();
    if (status === 'open') return 'open';
    if (status === 'closed') return 'closed';
    return null;
}

export function formatStalenessBucket(totalSeconds) {
    const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.ceil(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.ceil(seconds / 3600)}h`;
    return `${Math.ceil(seconds / 86400)}d`;
}

export function formatSyncTimestamp(value) {
    if (!value) return 'Unknown';
    const parsed = parseQuoteTimestamp(value);
    return !parsed || Number.isNaN(parsed.getTime()) ? 'Unknown' : formatDateTime(parsed);
}

export function normalizePriceRefreshStatusPayload(result, { hasNewerPrices, hasNewerFactorSnapshot } = {}) {
    return {
        ...result,
        latest_security_updated_at: result?.latest_security_updated_at ?? null,
        latest_factor_snapshot_date: result?.latest_factor_snapshot_date ?? null,
        factor_model: result?.factor_model ?? null,
        factor_data_model: result?.factor_data_model ?? null,
        updated_security_count_since: Number(result?.updated_security_count_since || 0),
        has_newer_prices: Boolean(
            hasNewerPrices === undefined ? result?.has_newer_prices : hasNewerPrices
        ),
        has_newer_factor_snapshot: Boolean(
            hasNewerFactorSnapshot === undefined
                ? result?.has_newer_factor_snapshot
                : hasNewerFactorSnapshot
        ),
    };
}

export function mergePriceRefreshOptions(currentOptions, nextOptions = {}) {
    const normalizedNext = {
        syncPosSizingDraft: nextOptions.syncPosSizingDraft ?? false,
        reason: nextOptions.reason ?? 'price_refresh',
    };

    if (!currentOptions) {
        return normalizedNext;
    }

    return {
        syncPosSizingDraft: Boolean(
            currentOptions.syncPosSizingDraft || normalizedNext.syncPosSizingDraft
        ),
        reason: normalizedNext.reason || currentOptions.reason || 'price_refresh',
    };
}

export function getSavedDraftReadinessBlocker(status) {
    const readiness = status?.saved_draft_readiness;
    if (!readiness || readiness.can_load_saved_draft !== false) {
        return null;
    }
    return readiness.blocker || {
        code: 'unknown',
        action: 'review',
        message: 'Saved pos-sizing draft prerequisites are not available.',
    };
}

function getMissingMarketDataCount(status) {
    return Number(
        status?.saved_draft_market_data_coverage?.pos_sizing_excluded_count
        ?? status?.watchlist_market_data_coverage?.pos_sizing_excluded_count
        ?? status?.watchlist_missing_market_data_count
        ?? status?.actual_missing_from_pos_sizing_count
        ?? 0
    );
}

function buildPosSizingImportRows(status, actualPortfolioRows, watchlistRows) {
    const actualRowsByIdentity = new Map();
    const rowsByIdentity = new Map();
    (actualPortfolioRows || []).forEach((row) => {
        const cogniveId = String(row?.cognive_id || '').trim();
        if (!cogniveId) return;
        actualRowsByIdentity.set(cogniveId, row);
        rowsByIdentity.set(cogniveId, row);
    });
    (watchlistRows || []).forEach((row) => {
        const cogniveId = String(row?.cognive_id || '').trim();
        if (!cogniveId) return;
        rowsByIdentity.set(cogniveId, {
            ...(rowsByIdentity.get(cogniveId) || {}),
            ...row,
        });
    });

    return (status?.actual_sync_additions || []).map((addition) => {
        const cogniveId = String(addition?.cognive_id || '').trim();
        const actualRow = actualRowsByIdentity.get(cogniveId) || {};
        const sourceRow = rowsByIdentity.get(cogniveId) || {};
        const quantity = toFiniteNumber(addition?.quantity ?? actualRow?.quantity);
        const price = toFiniteNumber(actualRow?.price ?? sourceRow?.price);
        const directGmv = toFiniteNumber(
            actualRow?.nmv ?? actualRow?.gmv ?? actualRow?.exp,
        );
        return {
            ...sourceRow,
            cognive_id: cogniveId,
            ticker: sourceRow?.ticker || addition?.ticker || null,
            quantity,
            // Actual additions receive their trade idea during Sync Draft.
            trade_idea: null,
            import_gmv: directGmv ?? (
                quantity !== null && price !== null ? quantity * price : null
            ),
            key: cogniveId,
        };
    }).filter((row) => row.cognive_id);
}

function buildPosSizingRemovalRows(status, additionalRemovalIds = []) {
    const removalIds = new Set(
        [...(status?.pos_sizing_missing_from_actual_cognive_ids || []), ...additionalRemovalIds]
            .map((value) => String(value || '').trim())
            .filter(Boolean),
    );
    if (removalIds.size === 0) return [];

    const savedRowsByIdentity = new Map(
        (status?.optimizer_context?.optimizer_rows || []).map((row) => [
            String(row?.cognive_id || '').trim(),
            row,
        ]),
    );
    return Array.from(removalIds).map((cogniveId) => {
        const savedRow = savedRowsByIdentity.get(cogniveId) || {};
        return {
            ...savedRow,
            cognive_id: cogniveId,
            removal_gmv: toFiniteNumber(savedRow?.pos_gmv_dollar),
            key: cogniveId,
        };
    });
}

function buildPosSizingDraftOnlyRows(status, watchlistRows) {
    const draftOnlyIds = new Set(
        (status?.pos_sizing_manual_draft_only_cognive_ids || [])
            .map((value) => String(value || '').trim())
            .filter(Boolean),
    );
    if (draftOnlyIds.size === 0) return [];

    // Labels remain displayable by exact Cognive identity when blocked readiness
    // withholds calculation rows. Never take draft amounts from the Watchlist.
    const watchlistRowsByIdentity = new Map(
        (watchlistRows || []).map((row) => [String(row?.cognive_id || '').trim(), row]),
    );
    const savedRowsByIdentity = new Map(
        (status?.optimizer_context?.optimizer_rows || []).map((row) => [
            String(row?.cognive_id || '').trim(),
            row,
        ]),
    );
    return Array.from(draftOnlyIds).map((cogniveId) => {
        const savedRow = savedRowsByIdentity.get(cogniveId) || {};
        return {
            ...watchlistRowsByIdentity.get(cogniveId),
            ...savedRow,
            cognive_id: cogniveId,
            trade_idea: savedRow?.trade_idea ?? null,
            draft_gmv: savedRow?.pos_gmv_dollar == null
                ? null : toFiniteNumber(savedRow.pos_gmv_dollar),
            key: cogniveId,
        };
    });
}

export function isPosSizingActualRecoveryAvailable(status, actualPortfolioIsReady) {
    return Boolean(status?.has_ideas === false && actualPortfolioIsReady === true);
}

export const POS_SIZING_ACTUAL_EMPTY_MESSAGE = (
    'The Actual portfolio is empty and no manual draft additions remain in the current Watchlist. '
    + 'Add at least one Watchlist security to Actual '
    + 'before syncing Position Sizing.'
);

// An accepted import whose holdings matched Actual: Actual is unchanged, but
// the draft has not been reset to those holdings yet.
function isUnconsumedActualReimport(status) {
    return status?.actual_import_unconsumed === true
        && status?.actual_parent_state_changed !== true;
}

function getPosSizingActualRemovalCount(status) {
    const ids = status?.pos_sizing_missing_from_actual_cognive_ids;
    return Array.isArray(ids)
        ? new Set(ids.map(value => String(value || '').trim()).filter(Boolean)).size
        : Number(status?.pos_sizing_missing_from_actual_count ?? 0);
}

function getPosSizingManualMembership(status, watchlistRows) {
    const manualIds = [...new Set(
        (status?.pos_sizing_manual_draft_only_cognive_ids || [])
            .map(value => String(value || '').trim()).filter(Boolean),
    )];
    if (!Array.isArray(watchlistRows)) {
        return { keptIds: manualIds, removedIds: [], known: false };
    }
    const watchlistIds = new Set(watchlistRows.map(row => String(row?.cognive_id || '').trim()));
    return {
        keptIds: manualIds.filter(id => watchlistIds.has(id)),
        removedIds: manualIds.filter(id => !watchlistIds.has(id)),
        known: true,
    };
}

// This is a UI prerequisite check. Publication still validates current membership,
// market/factor inputs and source fences before replacing the saved draft.
export function getPosSizingActualSourceBlocker(status, actualPortfolioIsReady, watchlistRows) {
    const manualMembership = getPosSizingManualMembership(status, watchlistRows);
    if (actualPortfolioIsReady == null || (
        actualPortfolioIsReady === false && !manualMembership.known
        && manualMembership.keptIds.length > 0
    )) {
        return { pending: true, message: 'Checking Actual and Watchlist membership before syncing Position Sizing.' };
    }
    if (actualPortfolioIsReady === false && manualMembership.keptIds.length === 0) {
        return { pending: false, message: POS_SIZING_ACTUAL_EMPTY_MESSAGE };
    }
    return null;
}

export function shouldSyncPosSizingFromActual(status, actualPortfolioIsReady) {
    const actualAdditionCount = Number(
        status?.actual_missing_from_pos_sizing_count ?? 0
    );
    return Boolean(
        isPosSizingActualRecoveryAvailable(status, actualPortfolioIsReady)
        || status?.requires_actual_sync
        || (actualAdditionCount > 0 && status?.provenance === 'auto')
    );
}

export const POS_SIZING_SAVED_FACTOR_DATA_BLOCKER_CODE = 'position_sizing_saved_factor_context_unavailable';

// Plain wording for a draft whose saved factor data no longer verifies. A
// price sync keeps share quantities and re-derives trade ideas from them.
export function describePosSizingSavedFactorDataSync(status, actualPortfolioIsReady = null) {
    const lead = 'This draft\'s saved factor data needs a refresh before Position Sizing can open it.';
    return shouldSyncPosSizingFromActual(status, actualPortfolioIsReady)
        ? `${lead} Sync Draft rebuilds the draft from your Actual portfolio.`
        : `${lead} Sync Draft keeps your share quantities and recalculates trade ideas from them.`;
}

export function buildPosSizingDraftFreshnessSummary(
    status,
    {
        actualPortfolioIsReady = false,
        actualPortfolioRows = [],
        watchlistRows = null,
    } = {},
) {
    const statusPending = !status;
    const requiresActualSourceSync = shouldSyncPosSizingFromActual(status, actualPortfolioIsReady);
    const actualSourceBlocker = requiresActualSourceSync
        ? getPosSizingActualSourceBlocker(status, actualPortfolioIsReady, watchlistRows)
        : null;
    const actualRecoveryReadinessPending = actualSourceBlocker?.pending || (
        status?.has_ideas === false && actualPortfolioIsReady == null
    );
    if (statusPending || actualRecoveryReadinessPending) {
        return {
            visible: true,
            loading: true,
            isStale: false,
            statusColor: 'info',
            freshnessLabel: 'Checking...',
            detailText: 'Checking pos-sizing freshness...',
            contextState: 'checking',
            contextComparisonRows: [],
        };
    }

    const actualAdditionCount = Number(
        status?.actual_missing_from_pos_sizing_count ?? 0
    );
    const actualRemovalCount = getPosSizingActualRemovalCount(status);
    const manualDraftOnlyCount = Number(
        status?.pos_sizing_manual_draft_only_count ?? 0
    );
    const requiresActualRecovery = isPosSizingActualRecoveryAvailable(
        status,
        actualPortfolioIsReady,
    );
    const actualSourceEmpty = Boolean(actualSourceBlocker);
    const manualMembership = getPosSizingManualMembership(status, watchlistRows);
    const hasNoSavedDraft = (
        status?.saved_draft_readiness?.can_load_saved_draft === false
        && status?.saved_draft_readiness?.blocker?.code === 'no_saved_draft'
    );

    if (
        !status?.has_ideas
        && !requiresActualRecovery
        && (hasNoSavedDraft || !status?.optimizer_context)
    ) {
        return { visible: false };
    }

    const durableContext = status?.optimizer_context;
    const durableFreshness = durableContext?.freshness;
    const durableContextIsRunnable = Boolean(
        ['current', 'stale', 'unknown'].includes(durableFreshness)
        && durableContext?.availability === 'available'
        && durableContext?.runnability === 'runnable'
    );
    const durableState = durableContextIsRunnable
        ? durableFreshness
        : (!durableContext || durableFreshness === 'unknown' ? 'unknown' : 'unavailable');
    const durableContextBlocked = !durableContextIsRunnable;
    const currentComparisonUnavailable = (
        durableContextIsRunnable && durableState === 'unknown'
    );
    const staleRows = status?.stale_rows ?? 0;
    const totalRows = status?.total_rows ?? 0;
    const maxStaleSeconds = status?.max_stale_seconds ?? 0;
    const staleReasons = getUserVisiblePosSizingStaleReasons(
        durableContext?.stale_reasons ?? [],
    );
    const contextStaleReasons = staleReasons.filter((reason) => reason !== 'prices');
    const missingMarketDataCount = getMissingMarketDataCount(status);
    const hasMissingMarketData = missingMarketDataCount > 0;
    const isStale = durableState === 'stale';
    const contextUpdateRequired = status?.context_update?.contract === 'pos-sizing-context-update.v1'
        && status.context_update.required === true;
    const requiresExplicitParentRepair = contextUpdateRequired && status?.actual_parent_state_unknown === true;
    const hasSyncActionableStaleness = (
        contextUpdateRequired || requiresActualSourceSync || durableContextBlocked || isStale
    );
    const isPriceStale = staleReasons.includes('prices');
    const isContextStale = contextStaleReasons.length > 0;
    const savedContext = durableContext?.saved_context ?? {};
    const currentContext = status?.current_context ?? {};
    const contextReasonText = contextStaleReasons.length > 0
        ? contextStaleReasons.map(formatPosSizingStaleReasonLabel).join(', ').toLowerCase()
        : 'the current construction context changed';
    const missingMarketDataDetail = hasMissingMarketData
        ? buildPosSizingMarketDataDetail(status)
        : null;
    const actualAdditionsDetail = actualAdditionCount > 0 && !actualSourceEmpty
        ? `${actualAdditionCount} Actual portfolio securit${actualAdditionCount === 1 ? 'y is' : 'ies are'} ready to import into Position Sizing.`
        : null;
    const actualRemovalsDetail = actualRemovalCount > 0 && !actualSourceEmpty
        ? `${actualRemovalCount} saved Position Sizing securit${actualRemovalCount === 1 ? 'y is' : 'ies are'} no longer in Actual and will be removed when you Sync Draft.`
        : null;

    let detailText = 'The saved Position Sizing context is current and available.';
    let usedMissingMarketDataDetail = false;
    let usedActualAdditionsDetail = false;
    let usedActualRemovalsDetail = false;
    if (actualSourceEmpty) {
        detailText = `${POS_SIZING_ACTUAL_EMPTY_MESSAGE} Your saved draft remains unchanged.`;
    } else if (requiresActualRecovery) {
        detailText = 'The saved pos-sizing draft is empty while the Actual portfolio has positions. Sync Draft to rebuild it from Actual.';
    } else if (durableContextBlocked) {
        detailText = (
            durableContext?.blocker?.code === POS_SIZING_SAVED_FACTOR_DATA_BLOCKER_CODE
                ? describePosSizingSavedFactorDataSync(status, actualPortfolioIsReady)
                : durableContext?.blocker?.message
        ) || 'The saved Position Sizing context cannot be verified. Sync Draft before loading it or running the Optimizer.';
        if (
            durableContext?.blocker?.action === 'sync_draft'
            && !/sync draft/i.test(detailText)
        ) {
            detailText += ' Sync Draft before loading it or running the Optimizer.';
        }
    } else if (requiresActualSourceSync) {
        detailText = isUnconsumedActualReimport(status)
            ? 'Your Actual portfolio was imported again after this Position Sizing draft was saved. Sync Draft to reset the draft to those holdings.'
            : 'Actual portfolio positions changed after this Position Sizing draft was saved. Sync Draft to rebuild it from Actual.';
    } else if (requiresExplicitParentRepair) {
        detailText = 'The saved draft remains available. Use Sync Draft once to verify its parent context before automatic updates can resume.';
    } else if (currentComparisonUnavailable) {
        detailText = 'The exact saved Position Sizing context is available and runnable, but comparison with the current app context is temporarily unavailable. Optimizer runs keep the draft holdings, trade ideas and expected returns, and reload current Watchlist factor data, prices and settings. Sync Draft is not required to run.';
    } else if (isContextStale && isPriceStale) {
        detailText = `The saved Position Sizing draft is available. Optimizer runs keep its holdings, trade ideas and expected returns, and reload current Watchlist factor data, prices and settings. It differs from the current context because ${contextReasonText}, and newer prices are available. Sync Draft to align it with current settings.`;
    } else if (isContextStale) {
        detailText = `The saved Position Sizing draft is available. Optimizer runs keep its holdings, trade ideas and expected returns, and reload current Watchlist factor data, prices and settings. It differs from the current context because ${contextReasonText}. Sync Draft to align it with current settings.`;
    } else if (isStale && staleRows > 0) {
        detailText = `${staleRows} of ${totalRows} names have newer prices than this saved Position Sizing context. The saved context remains available and runnable. Sync Draft is optional and will refresh trade ideas and sizing.`;
    } else if (isStale) {
        detailText = 'The saved Position Sizing draft is stale but remains available. Optimizer runs keep its holdings, trade ideas and expected returns, and reload current Watchlist factor data, prices and settings. Sync Draft to align it with the current context.';
    } else if (actualAdditionsDetail) {
        detailText = actualAdditionsDetail;
        usedActualAdditionsDetail = true;
    } else if (actualRemovalsDetail) {
        detailText = actualRemovalsDetail;
        usedActualRemovalsDetail = true;
    } else if (missingMarketDataDetail) {
        detailText = `${missingMarketDataDetail} This does not affect the freshness of the current draft.`;
        usedMissingMarketDataDetail = true;
    } else if (status?.manual_edits_present) {
        detailText = manualDraftOnlyCount > 0
            ? `${manualDraftOnlyCount} manually added Position Sizing securit${manualDraftOnlyCount === 1 ? 'y remains' : 'ies remain'} in this draft independently of Actual.`
            : 'Current pos-sizing draft includes manual edits and is aligned with the latest prices.';
    }
    if (actualAdditionsDetail && !usedActualAdditionsDetail) {
        detailText += ` ${actualAdditionsDetail}`;
    }
    if (actualRemovalsDetail && !usedActualRemovalsDetail) {
        detailText += ` ${actualRemovalsDetail}`;
    }
    if (requiresActualSourceSync && !actualSourceEmpty && manualMembership.keptIds.length > 0) {
        detailText += ' Manual additions still in the current Watchlist are preserved if their required data is available; otherwise the saved draft remains unchanged.';
    }
    if (requiresActualSourceSync && !actualSourceEmpty && manualMembership.removedIds.length > 0) {
        const removedCount = manualMembership.removedIds.length;
        detailText += ` ${removedCount} manual draft addition${removedCount === 1 ? ' is' : 's are'} no longer in the current Watchlist and will be removed when you Sync Draft.`;
    }
    if (missingMarketDataDetail && !usedMissingMarketDataDetail) {
        detailText += ` ${missingMarketDataDetail}`;
    }
    let freshnessLabel = 'Current';
    if (actualSourceEmpty) {
        freshnessLabel = 'Actual Empty';
    } else if (requiresActualRecovery || requiresExplicitParentRepair) {
        freshnessLabel = 'Sync Required';
    } else if (requiresActualSourceSync && !isStale) {
        freshnessLabel = 'Sync Required';
    } else if (durableContextBlocked) {
        freshnessLabel = durableState === 'unknown'
            ? 'Context Unknown'
            : 'Context Unavailable';
    } else if (currentComparisonUnavailable) {
        freshnessLabel = 'Comparison Unavailable';
    } else if (isStale) {
        freshnessLabel = isPriceStale && !isContextStale
            ? `Stale ${formatStalenessBucket(maxStaleSeconds)}`
            : 'Stale';
    }
    const importRows = buildPosSizingImportRows(
        status,
        actualPortfolioRows,
        watchlistRows,
    );
    const removalRows = buildPosSizingRemovalRows(
        status, requiresActualSourceSync ? manualMembership.removedIds : [],
    );
    const draftOnlyRows = buildPosSizingDraftOnlyRows(status, watchlistRows)
        .filter(row => !requiresActualSourceSync || manualMembership.keptIds.includes(row.cognive_id));
    return {
        visible: true,
        loading: false,
        isStale: actualSourceEmpty || requiresActualSourceSync || isStale,
        statusColor: (
            actualSourceEmpty || requiresActualSourceSync || requiresExplicitParentRepair || durableContextBlocked || isStale
        )
            ? 'warning'
            : currentComparisonUnavailable
                ? 'info'
                : 'success',
        freshnessLabel,
        detailText,
        contextState: requiresActualRecovery ? 'unavailable' : durableState,
        contextComparisonRows: [
            ...buildPosSizingContextComparisonRows(savedContext, currentContext, status),
            ...(requiresExplicitParentRepair ? [{
                key: 'parent_context_verification',
                label: 'Parent context',
                savedValue: 'Not recorded; Sync Draft to verify',
                currentValue: 'Verification required',
                matches: null,
            }] : []),
        ],
        syncActionVisible: hasSyncActionableStaleness && !actualSourceEmpty,
        importRows,
        removalRows,
        draftOnlyRows,
    };
}

export function toFiniteNumber(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
}

export function resolveHoldingQuantity(holding, position) {
    const positionQty = toFiniteNumber(position?.quantity);
    if (positionQty !== null) return positionQty;

    const holdingQty = toFiniteNumber(holding?.quantity);
    if (holdingQty !== null) return holdingQty;

    // Last-resort estimate from risk payload fields when positions are still refreshing.
    const nmv = toFiniteNumber(holding?.nmv);
    const price = toFiniteNumber(position?.price ?? holding?.price);
    if (nmv !== null && price !== null && price !== 0) {
        return nmv / price;
    }
    return null;
}

export function normalizeSedolList(values = []) {
    const normalized = [];
    const seen = new Set();

    values.forEach((value) => {
        const sedol = value == null ? '' : String(value).trim();
        if (!sedol || seen.has(sedol)) return;
        seen.add(sedol);
        normalized.push(sedol);
    });

    return normalized;
}

export function buildSelectedOverviewRefreshContextKey({
    targetKey,
    watchlistVersion,
    posSizingVersion = 0,
    invalidationVersion = 0,
    riskContext,
} = {}) {
    return JSON.stringify([
        targetKey ?? '',
        Number(watchlistVersion || 0),
        targetKey === 'pos' ? Number(posSizingVersion || 0) : 0,
        Number(invalidationVersion || 0),
        String(riskContext?.factorModel ?? riskContext?.factor_model ?? '').trim(),
        String(riskContext?.factorDataModel ?? riskContext?.factor_data_model ?? '').trim(),
        String(riskContext?.factorSnapshotDate ?? riskContext?.factor_snapshot_date ?? '').trim(),
        String(riskContext?.betaSource ?? riskContext?.beta_source ?? '').trim(),
        String(riskContext?.idioVolSource ?? riskContext?.idio_vol_source ?? '').trim(),
        Boolean(riskContext?.usesSavedSnapshot),
    ]);
}

export function extractBackendErrorText(error) {
    const message = error?.message;
    if (typeof message !== 'string' || message.length === 0) {
        return '';
    }

    const jsonStart = message.indexOf('{');
    if (jsonStart < 0) {
        return message;
    }

    try {
        const parsed = JSON.parse(message.slice(jsonStart));
        const detail = parsed?.detail;
        if (typeof detail === 'string' && detail.trim()) {
            return detail;
        }
        if (typeof detail?.message === 'string' && detail.message.trim()) {
            return detail.message;
        }
        if (typeof parsed?.message === 'string' && parsed.message.trim()) {
            return parsed.message;
        }
        return message;
    } catch (_) {
        return message;
    }
}

export function isExpectedLegacySnapshotSyncError(error) {
    const text = `${extractBackendErrorText(error)} ${String(error?.message || '')}`.toLowerCase();
    return (
        text.includes('predates market snapshots') ||
        text.includes('sync draft to enable editing')
    );
}

export function isExpectedNoWatchlistMatrixError(error) {
    const text = `${extractBackendErrorText(error)} ${String(error?.message || '')}`.toLowerCase();
    return (
        text.includes('no cognive_id rows found for matrices') ||
        text.includes('no cognive id rows found for matrices') ||
        text.includes('no sedols found for matrices') ||
        text.includes('no sedols found') ||
        text.includes('no identifiers found for matrices') ||
        text.includes('no identifiers found') ||
        text.includes('watchlist is empty')
    );
}

export function isExpectedSavedDraftPrerequisiteError(error) {
    const text = `${extractBackendErrorText(error)} ${String(error?.message || '')}`.toLowerCase();
    return (
        text.includes('position_sizing_saved_factor_context_unavailable') ||
        text.includes('position_sizing_saved_factor_return_artifact_unavailable') ||
        text.includes('position_sizing_saved_factor_matrix_artifact_unavailable') ||
        text.includes('position_sizing_durable_context_input_unavailable') ||
        text.includes('position_sizing_durable_context_unknown_legacy') ||
        text.includes('position_sizing_context_verification_changed') ||
        text.includes('no barra ids found for provided sedols on latest as_of_date') ||
        text.includes('no barra ids found for provided identifiers on latest as_of_date') ||
        text.includes('no axioma ids found for provided sedols on the latest as_of_date') ||
        text.includes('no axioma ids found for provided identifiers on the latest as_of_date') ||
        text.includes('no factors found for model') ||
        text.includes('saved pos-sizing draft references factor data that is no longer available') ||
        text.includes('saved pos-sizing draft is missing required context metadata')
    );
}

export function hasMatrixPayloadEntries(payload) {
    return Boolean(payload) && Object.keys(payload).length > 0;
}

export function flattenFactorGroups(factorGroups, factorGroupOrder = []) {
    if (!factorGroups || typeof factorGroups !== 'object') {
        return [];
    }

    const orderedKeys = Array.isArray(factorGroupOrder) && factorGroupOrder.length
        ? factorGroupOrder
        : Object.keys(factorGroups);

    return orderedKeys.flatMap((groupLabel) => (
        Array.isArray(factorGroups[groupLabel]) ? factorGroups[groupLabel] : []
    ));
}

export function formatPercentLabel(value, { absolute = false, digits = 1 } = {}) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return 'N/A';

    const pct = (absolute ? Math.abs(amount) : amount) * 100;
    const sign = !absolute && pct > 0 ? '+' : '';
    return `${sign}${pct.toFixed(digits)}%`;
}
